import { CoverageLevel, ExpensesUsageOptionType } from '@/enums';
import {
    ComparePlanResponse,
    FutureExpenses,
    IncurredExpenseResponse,
    MedicalAndPrescriptionSelectedExpenses,
    PlanResponse,
    PlanComparisonToolState,
    PlanComparisonToolStep,
    SelectedExpenses,
    ServiceCategory,
    Plan
} from '@/types';
import { defineStore } from 'pinia';

export const initialSteps: PlanComparisonToolStep[] = [
    {
        componentName: 'coverage-basics',
        title: 'planComparisonTool.coverageBasics.title',
        active: true,
        step: 1
    },
    {
        componentName: 'future-expenses',
        title: 'planComparisonTool.futureExpenses.title',
        active: false,
        step: 2
    },
    {
        componentName: 'compare-plans',
        title: 'planComparisonTool.comparePlans.title',
        active: false,
        step: 3
    },
    {
        componentName: 'view-comparison',
        title: 'planComparisonTool.viewComparison.title',
        active: false,
        step: 4
    }
];

const initialFormFields = {
    coverageBasics: {
        isSpouseCovered: false,
        isChildrenCovered: false,
        coverageChildCount: undefined,
        highCostMemberExists: false,
        highCostMemberName: ''
    },
    comparePlans: {
        hraHiaRolloverAmount: undefined,
        grossHouseholdIncome: undefined,
        taxFilingStatus: ''
    },
    rxTotals: {
        grx: 0,
        prx: 0,
        nrx: 0,
        srx: 0
    }
};

const initialSelectedExpenses: SelectedExpenses = {
    previousYear: {
        medical: [],
        prescriptionDrugs: []
    },
    lowUsage: {
        medical: [],
        prescriptionDrugs: []
    },
    mediumUsage: {
        medical: [],
        prescriptionDrugs: []
    },
    highUsage: {
        medical: [],
        prescriptionDrugs: []
    },
    customUsage: {
        medical: [],
        prescriptionDrugs: []
    },
    nonChangingPreviousYear: {
        medical: [],
        prescriptionDrugs: []
    }
};

const initialFutureExpenses: FutureExpenses = {
    selectedOption: ExpensesUsageOptionType.PREVIOUS_YEAR_USAGE,
    selectedExpenses: initialSelectedExpenses
};

const initialServiceCategories: Array<ServiceCategory> = [];

const initialIncurredExpenses: IncurredExpenseResponse = {
    medicalExpenses: [],
    totalPriorYearExpense: 0
};

const initialPlans: PlanResponse = {
    hideHraHsa: false,
    plans: [] as Plan[]
};

/**
 * pinia store module definition used for plan comparison tool
 */
export const usePlanComparisonToolStore = defineStore('planComparisonToolStore', {
    state: () => {
        const state: PlanComparisonToolState = {
            steps: JSON.parse(JSON.stringify(initialSteps)) as PlanComparisonToolStep[],
            formFields: {
                coverageBasics: initialFormFields.coverageBasics,
                comparePlans: initialFormFields.comparePlans,
                rxTotals: initialFormFields.rxTotals
            },
            futureExpenses: initialFutureExpenses,
            serviceCategories: initialServiceCategories,
            incurredExpenses: initialIncurredExpenses,
            plansResponse: initialPlans,
            plansPremiumHsaAmounts: {},
            selectedPlanIdsToCompare: [],
            comparePlans: []
        };
        return state;
    },
    getters: {
        isNonSecure: (state) => {
            return state.sessionToken !== undefined;
        },
        isSecure: (state) => {
            return state.sessionToken === undefined;
        },
        activeStep: (state) => {
            return state.steps.find((step) => step.active);
        },
        totalSteps: (state) => {
            return state.steps.length;
        },
        selectedCoverageLevel: (state) => {
            let coverageLevel = CoverageLevel.SELF;
            const { isChildrenCovered, isSpouseCovered } = state.formFields.coverageBasics;
            if (isSpouseCovered) {
                coverageLevel = CoverageLevel.SELF_SPOUSE;
            }
            if (isChildrenCovered) {
                coverageLevel = CoverageLevel.SELF_CHILDREN;
            }
            if (isSpouseCovered && isChildrenCovered) {
                coverageLevel = CoverageLevel.FAMILY;
            }
            return coverageLevel;
        },
        highCostMemberExists: (state) => {
            const { highCostMemberExists, isChildrenCovered, isSpouseCovered } = state.formFields.coverageBasics;
            // high cost member can only exist if children and/or spouse is covered
            if (isChildrenCovered || isSpouseCovered) {
                return highCostMemberExists;
            }
            return false;
        },
        selectedFutureExpenses: (state) => {
            switch (state.futureExpenses.selectedOption) {
                case ExpensesUsageOptionType.PREVIOUS_YEAR_USAGE:
                    return state.futureExpenses.selectedExpenses.previousYear;
                case ExpensesUsageOptionType.LOW_USAGE:
                    return state.futureExpenses.selectedExpenses.lowUsage;
                case ExpensesUsageOptionType.MEDIUM_USAGE:
                    return state.futureExpenses.selectedExpenses.mediumUsage;
                case ExpensesUsageOptionType.HIGH_USAGE:
                    return state.futureExpenses.selectedExpenses.highUsage;
                default:
                    return state.futureExpenses.selectedExpenses.customUsage;
            }
        }
    },
    actions: {
        nextStep() {
            let index = this.steps.findIndex((step) => step.active);
            if (index < this.steps.length - 1) {
                index++;
            }
            this.makeStepActive(index);
        },
        previousStep() {
            let index = this.steps.findIndex((step) => step.active);
            if (index > 0) {
                index--;
            }
            this.makeStepActive(index);
        },
        makeStepActive(index: number) {
            this.steps.forEach((step) => step.active = false);
            this.steps[index].active = true;
        },
        updateSelectedExpensesPreviousYear(payload: MedicalAndPrescriptionSelectedExpenses) {
            this.futureExpenses.selectedExpenses.previousYear.medical = payload.medical;
            this.futureExpenses.selectedExpenses.previousYear.prescriptionDrugs = payload.prescriptionDrugs;
        },
        updateNonChangingSelectedExpensesPreviousYear(payload: MedicalAndPrescriptionSelectedExpenses) {
            this.futureExpenses.selectedExpenses.nonChangingPreviousYear.medical = payload.medical;
            this.futureExpenses.selectedExpenses.nonChangingPreviousYear.prescriptionDrugs = payload.prescriptionDrugs;
        },
        updateSelectedExpensesLowUsage(payload: MedicalAndPrescriptionSelectedExpenses) {
            this.futureExpenses.selectedExpenses.lowUsage.medical = payload.medical;
            this.futureExpenses.selectedExpenses.lowUsage.prescriptionDrugs = payload.prescriptionDrugs;
        },
        updateSelectedExpensesMediumUsage(payload: MedicalAndPrescriptionSelectedExpenses) {
            this.futureExpenses.selectedExpenses.mediumUsage.medical = payload.medical;
            this.futureExpenses.selectedExpenses.mediumUsage.prescriptionDrugs = payload.prescriptionDrugs;
        },
        updateSelectedExpensesHighUsage(payload: MedicalAndPrescriptionSelectedExpenses) {
            this.futureExpenses.selectedExpenses.highUsage.medical = payload.medical;
            this.futureExpenses.selectedExpenses.highUsage.prescriptionDrugs = payload.prescriptionDrugs;
        },
        updateSelectedExpensesCustomUsage(payload: MedicalAndPrescriptionSelectedExpenses) {
            this.futureExpenses.selectedExpenses.customUsage.medical = payload.medical;
            this.futureExpenses.selectedExpenses.customUsage.prescriptionDrugs = payload.prescriptionDrugs;
        },
        updateSelectedPlanIdsToCompare(payload: string[]) {
            this.selectedPlanIdsToCompare = payload;
        },
        updateComparePlans(payload: ComparePlanResponse[]) {
            this.comparePlans = payload;
        },
        /**
         * reset store state
         */
        reset() {
            this.initializeSteps();
            this.initializeForm();
            this.resetFutureExpenses();
            this.resetServiceCategories();
            this.resetIncurredExpenses();
            this.resetPremiumHsaAmounts();
            this.resetSelectedPlanIds();
            this.resetComparePlans();
        },
        initializeForm() {
            this.formFields.coverageBasics = JSON.parse(JSON.stringify(initialFormFields.coverageBasics));
        },
        initializeSteps() {
            this.steps = JSON.parse(JSON.stringify(initialSteps));
        },
        resetFutureExpenses() {
            this.futureExpenses = JSON.parse(JSON.stringify(initialFutureExpenses));
            if (this.isNonSecure) {
                // default to LOW usage when non-secure
                this.futureExpenses.selectedOption = ExpensesUsageOptionType.LOW_USAGE;
            }
        },
        resetServiceCategories() {
            this.serviceCategories = JSON.parse(JSON.stringify(initialServiceCategories));
        },
        resetIncurredExpenses() {
            this.incurredExpenses = JSON.parse(JSON.stringify(initialIncurredExpenses));
        },
        resetSelectedPlanIds() {
            this.selectedPlanIdsToCompare = [];
        },
        resetComparePlans() {
            this.comparePlans = [];
        },
        resetPremiumHsaAmounts() {
            this.formFields.comparePlans.hraHiaRolloverAmount = undefined;
            this.plansPremiumHsaAmounts = {};
        }
    }
});

export type PlanComparisonToolStore = ReturnType<typeof usePlanComparisonToolStore>;
